import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { AlertTriangle, Home } from 'lucide-react';
import { getModuleFromRoute, isBaseRole } from '../utils/routeToModuleMap';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredPermission?: string | string[];
  requireAllPermissions?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  requiredPermission,
  requireAllPermissions = false,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { hasRole, hasPermission, permissionsLoading } = usePermissions();
  const { data: userPermissions } = useUserPermissions();
  const location = useLocation();

  console.log('🚪 [ProtectedRoute] Estado actual:', {
    path: location.pathname,
    isAuthenticated,
    isLoading,
    permissionsLoading,
    hasUser: !!user,
    userRole: user?.rol,
  });

  // Mostrar loader mientras se verifica la autenticación o se cargan permisos
  if (isLoading || permissionsLoading) {
    console.log('⏳ [ProtectedRoute] Mostrando loader:', {
      reason: isLoading ? 'Verificando autenticación' : 'Cargando permisos',
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isLoading
              ? 'Verificando autenticación...'
              : 'Cargando permisos...'}
          </p>
        </div>
      </div>
    );
  }

  // Redirigir al login si no está autenticado
  if (!isAuthenticated) {
    console.log(
      '🚪 [ProtectedRoute] Usuario no autenticado, redirigiendo a /login'
    );
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log(
    '✅ [ProtectedRoute] Usuario autenticado, permitiendo acceso a:',
    location.pathname
  );

  // Verificar roles si se especificaron.
  //
  // Un usuario con rol personalizado (rol_id que no es uno de los 4 roles base)
  // arrastra igual un valor de `usuarios.rol` porque esa columna es un ENUM
  // NOT NULL. Antes eso hacía que el chequeo por rol base ganara siempre: un
  // `encargado_inventario` guardado con rol='chef' alcanzaba /recetas,
  // /minutas, /cocina y /pedidos escribiendo la URL, aunque el sidebar
  // (que filtra por permisos dinámicos) no le mostrara esos enlaces.
  //
  // Ahora, cuando el usuario tiene rol personalizado, manda el permiso
  // dinámico del módulo de la ruta y no el rol base heredado.
  const tieneRolPersonalizado =
    !!user?.rol_id && !!userPermissions && !isBaseRole(userPermissions.rol_nombre);

  if (tieneRolPersonalizado) {
    const currentModule = getModuleFromRoute(location.pathname);
    const moduleData = currentModule
      ? userPermissions?.modulos?.[currentModule]
      : undefined;

    if (!moduleData?.permisos?.ver) {
      return (
        <AccessDeniedScreen
          reason="permission"
          required={currentModule ? `${currentModule}.ver` : 'módulo desconocido'}
          current={user?.rol_display || user?.rol}
          customMessage={`Tu rol (${user?.rol_display || user?.rol}) no tiene acceso a este módulo.`}
        />
      );
    }
  } else if (allowedRoles.length > 0) {
    // Primero verificar si tiene el rol exacto (para roles base)
    const hasRequiredRole = allowedRoles.some((role) => hasRole(role));

    // Si no tiene el rol exacto y NO es un rol base, verificar permisos dinámicos
    if (!hasRequiredRole && user?.rol && !isBaseRole(user.rol)) {
      // El usuario tiene un rol personalizado, verificar permisos de módulo
      const currentModule = getModuleFromRoute(location.pathname);

      if (currentModule && userPermissions?.modulos) {
        const moduleData = userPermissions.modulos[currentModule];
        // Si tiene permiso de "ver" en el módulo, permitir acceso
        const hasModuleAccess = moduleData?.permisos?.ver ?? false;

        if (!hasModuleAccess) {
          return (
            <AccessDeniedScreen
              reason="role"
              required={allowedRoles}
              current={user?.rol}
              customMessage={`No tienes permiso para acceder a este módulo. Tu rol: ${user.rol_display || user.rol}`}
            />
          );
        }
      } else if (!hasRequiredRole) {
        // No se pudo determinar el módulo o no hay permisos, denegar
        return (
          <AccessDeniedScreen
            reason="role"
            required={allowedRoles}
            current={user?.rol}
          />
        );
      }
    } else if (!hasRequiredRole) {
      // Es un rol base pero no está en allowedRoles
      return (
        <AccessDeniedScreen
          reason="role"
          required={allowedRoles}
          current={user?.rol}
        />
      );
    }
  }

  // Verificar permisos específicos si se especificaron
  if (requiredPermission) {
    let hasRequiredPermission = false;

    if (Array.isArray(requiredPermission)) {
      if (requireAllPermissions) {
        hasRequiredPermission = requiredPermission.every((p) =>
          hasPermission(p)
        );
      } else {
        hasRequiredPermission = requiredPermission.some((p) =>
          hasPermission(p)
        );
      }
    } else {
      hasRequiredPermission = hasPermission(requiredPermission);
    }

    if (!hasRequiredPermission) {
      return (
        <AccessDeniedScreen
          reason="permission"
          required={requiredPermission}
          current={user?.rol}
        />
      );
    }
  }

  return <>{children}</>;
};

interface AccessDeniedScreenProps {
  reason: 'role' | 'permission';
  required: string | string[];
  current?: string;
  customMessage?: string;
}

const AccessDeniedScreen: React.FC<AccessDeniedScreenProps> = ({
  reason,
  required,
  current,
  customMessage,
}) => {
  const requiredText = Array.isArray(required) ? required.join(', ') : required;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center p-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Acceso Denegado
          </h1>

          <p className="text-gray-600 mb-6">
            {customMessage ||
              'No tienes los permisos necesarios para acceder a esta sección.'}
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
            <p className="font-medium text-gray-900 mb-2">
              {reason === 'role' ? 'Rol requerido:' : 'Permiso requerido:'}
            </p>
            <p className="text-gray-600 mb-3">{requiredText}</p>

            {current && (
              <>
                <p className="font-medium text-gray-900 mb-2">Tu rol actual:</p>
                <p className="text-gray-600">{current}</p>
              </>
            )}
          </div>

          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Home className="h-4 w-4 mr-2" />
            Regresar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProtectedRoute;
